import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, useWatch } from 'react-hook-form'
import { useSnackbar } from 'notistack'
// @mui
import {
  Alert,
  Button,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Grid,
  Stack,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { ChevronLeft } from '@mui/icons-material'
// components
import Iconify from 'components/Iconify'
import { FormProvider, RHFTextField } from 'components/hook-form'
import { StyledBaseContainer } from '~/commons/styled'
import Select from '~/commons/components/Select'
// hooks
import { usePreventUnsavedChanges } from 'hooks/usePreventUnsavedChanges'
import { useUserKYC } from 'domains/Users/Verification/hooks/useVerificationUser'
import { useBoundVerificationUserStore } from 'domains/Users/Verification/stores'
import { useBoundBalanceStore } from 'domains/Balance/store'
// helpers
import { getErrorMessage } from 'helpers/error'
// services
import { putBankAccountKYC } from '~/services/user'
// types
import { IBankAccount } from 'domains/Users/Verification/stores/VerificationUser/interface'
import useAuth from 'hooks/useAuth'
import trackEvent from '~/trackers'
import { PATH_DASHBOARD } from 'routes/paths'
import useLocales from 'hooks/useLocales'

const DEFAULT_STATE_BANK_ACCOUNT_STATE = {
  bank_account_name: '',
  bank_account_no: '',
  bank_id: null,
  is_different_name: false,
}

const disableStyleDefault = {
  '& .MuiSelect-select, .Mui-disabled': {
    backgroundColor: 'unset !important',
  },
  '& svg': {
    backgroundColor: 'unset !important',
  },
}

const AlertMain = styled(Alert)({
  marginBottom: '24px',
  padding: '12px',
  '& .MuiAlert-icon': {
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
})

interface IBackAccountFormProps {
  isEdit?: boolean
  setActiveStepEditBank?: Function
}

export default function BankAccountForm({
  isEdit,
  setActiveStepEditBank,
}: IBackAccountFormProps) {
  const { t } = useLocales()
  const { isReady, query, pathname, push } = useRouter()
  const { enqueueSnackbar } = useSnackbar()
  const { user } = useAuth()
  const trackerData = { query, pathname, user }

  const BANK_ACCOUNT_SCHEMA = Yup.object().shape({
    bank_id: Yup.string()
      .test({
        name: 'valid-bank-dropdown',
        test(value, { createError }) {
          if (value === 'placeholder') {
            return createError({
              message: t('key_bank_name_must_be_filled_in'),
            })
          }
          return true
        },
      })
      .required(t('key_must_be_filled')),
    bank_account_name: Yup.string()
      .required(
        t('key_the_name_of_the_account_owner_must_be_filled_in'),
      )
      .test(
        'len',
        t(
          'key_account_holder_name_must_be_a_minimum_of_3_characters',
        ),
        (val) => {
          if (val == undefined) {
            return true
          }
          return val.length >= 3
        },
      ),
    bank_account_no: Yup.string()
      .test({
        name: 'validate-bank-account-no',
        test(value, { createError }) {
          if (value === '') {
            return createError({
              message: t('key_account_number_must_be_filled_in'),
            })
          }
          return true
        },
      })
      .test(
        'len',
        t('key_minimum_account_number_3_characters'),
        (val) => {
          if (val == undefined) {
            return true
          }
          return val.length >= 3
        },
      ),
  })
  const form = useForm<IBankAccount>({
    resolver: yupResolver(BANK_ACCOUNT_SCHEMA),
    defaultValues: DEFAULT_STATE_BANK_ACCOUNT_STATE,
    mode: 'onChange',
  })

  const { setValue, trigger, control, handleSubmit, formState } = form
  const [bank_id, bank_account_name, bank_account_no] = useWatch({
    control,
    name: [
      'bank_id',
      'bank_account_name',
      'bank_account_no',
      'is_different_name',
    ],
  })
  const { isValid } = formState

  const { refetch: getUserKYC } = useUserKYC()

  const { setActiveStep, personalData, setTempBankAccount } =
    useBoundVerificationUserStore()
  const { getBankList, bankList, isLoadingBankList } =
    useBoundBalanceStore((state) => ({
      bankList: state.bankList,
      getBankList: state.getBankList,
      isLoadingBankList: state.isLoadingBankList,
    }))

  const { unsubscribeToRouteChangeGuard } = usePreventUnsavedChanges(
    '/dashboard/verification',
  )

  const [agreeWithTerms, setAgreeWithTerms] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isBankAccountNameDiff, setIsBankAccountNameDiff] =
    useState(false)

  const onActionButtonSubmit = async (data) => {
    try {
      setIsLoading(true)
      const payload = {
        ...data,
        bank_id: Number(data.bank_id),
      }
      if (isEdit) {
        setTempBankAccount(payload)
        unsubscribeToRouteChangeGuard()
        getUserKYC()
        setActiveStepEditBank(1)
      } else {
        const result = await putBankAccountKYC(payload)
        if (result?.data?.code === 'SUCCESS') {
          unsubscribeToRouteChangeGuard()
          getUserKYC()
          setActiveStep(2)
          push(
            PATH_DASHBOARD.general.verification.root +
              '?source=BANK_ACCOUNT&source_action=click_next_to_upload_document',
          )
        }
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (isReady) {
      getBankList()
      trackEvent.user('visit_bank_account', trackerData)
    }
  }, [isReady])

  useEffect(() => {
    if (personalData) {
      setValue('bank_account_name', personalData.bank_account_name)
      setValue('bank_account_no', personalData.bank_account_no)
      setValue('bank_id', personalData.bank_id)
      setValue('is_different_name', personalData.is_different_name)
      if (personalData.bank_account_name) {
        checkBankAccountNameDiff()
      }
      if (personalData.is_different_name) {
        setAgreeWithTerms(true)
      }
      if (
        personalData.bank_account_name ||
        personalData.bank_account_no ||
        personalData.bank_id
      ) {
        trigger()
      }
    }
  }, [personalData])

  const checkBankAccountNameDiff = () => {
    if (bank_account_name) {
      const isDiff = personalData.full_name !== bank_account_name
      setIsBankAccountNameDiff(isDiff)
      setValue('is_different_name', isDiff)
    }
  }

  useEffect(() => {
    if (bank_account_name) {
      checkBankAccountNameDiff()
    }
  }, [bank_account_name])

  const selectedBank = bankList?.find(
    (bank) => bank.bank_id === bank_id,
  )

  return (
    <FormProvider
      methods={form}
      onSubmit={handleSubmit(onActionButtonSubmit)}
    >
      <StyledBaseContainer>
        <Grid container spacing={3}>
          <Grid
            item
            gap={'4px'}
            display={'flex'}
            flexDirection={'column'}
          >
            <Typography variant="h6">
              {t('key_bank_account')}
            </Typography>
            <Typography
              variant="body2"
              sx={(theme) => ({ color: theme.palette.grey[600] })}
            >
              {t('key_completing_your_account_detail')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Select
              id="s-bank-name"
              required
              name="bank_id"
              placeholder={t('key_bank_name_*')}
              value={bank_id || ('placeholder' as any)}
              options={bankList.map((item) => {
                return { name: item.bank_id, label: item.bank_name }
              })}
              disabled={isLoadingBankList}
              onChange={(e) => {
                let value = e.target.value
                trackEvent.user('select_bank_name', trackerData, {
                  bank_name: bankList.find(
                    (item) => item.bank_id === Number(value),
                  )?.bank_name,
                  bank_id: Number(value),
                })
                setValue(
                  'bank_id',
                  // The Select yields strings and form state keeps the id as
                  // one until onActionButtonSubmit coerces it with Number();
                  // the yup schema validates it as a string too. IBankAccount
                  // types bank_id as the number the API returns, hence the
                  // cast. Coercing here instead would turn the placeholder's
                  // '' into 0 and satisfy the required check with a bank that
                  // does not exist.
                  (value !== 'placeholder'
                    ? value
                    : '') as unknown as number,
                  {
                    shouldValidate: true,
                  },
                )
              }}
              onBlur={() => trigger('bank_id')}
              sx={{ ...disableStyleDefault }}
            />
          </Grid>
          <Grid item xs={12}>
            <RHFTextField
              id="i-users-bankaccountform-bank-account-no"
              required
              name="bank_account_no"
              label={t('key_bank_account_number')}
              value={bank_account_no}
              placeholder={t('key_bank_account_number')}
              onChange={(e) => {
                let value = e.target.value
                if (value === '') {
                  setValue('bank_account_no', '', {
                    shouldValidate: true,
                  })
                } else if (value.match(/^\d+$/)) {
                  setValue('bank_account_no', value, {
                    shouldValidate: true,
                  })
                }
              }}
              onFocus={() =>
                trackEvent.user(
                  'click_bank_account_fill_in',
                  trackerData,
                )
              }
            />
          </Grid>
          <Grid item xs={12}>
            <RHFTextField
              id="i-users-bankaccountform-bank-account-name"
              required
              name="bank_account_name"
              label={t('key_account_holder_name')}
              value={bank_account_name}
              placeholder={t('key_account_holder_name')}
              onFocus={() =>
                trackEvent.user(
                  'click_bank_account_name_fill_in',
                  trackerData,
                )
              }
            />
          </Grid>
          {isBankAccountNameDiff && (
            <>
              <Grid item xs={12}>
                <AlertMain severity="info">
                  <Stack gap={2}>
                    <Typography variant="subtitle1">
                      {t('key_difference_between_identity')}
                    </Typography>
                    <Typography variant="body2">
                      {t('key_creator_agreement')}
                    </Typography>
                    <Stack gap={0.5}>
                      <Typography variant="body2">{`${t(
                        'key_account_holder_name',
                      )} :  ${bank_account_name}`}</Typography>
                      <Typography variant="body2">{`${t(
                        'key_account_owner_no',
                      )} : ${bank_account_no}`}</Typography>
                      <Typography variant="body2">{`${t(
                        'key_bank_name',
                      )}: ${
                        selectedBank?.bank_name ?? ''
                      }`}</Typography>
                    </Stack>
                    <Stack gap={1}>
                      <Typography variant="body2">
                        {t('key_creator_agreement1')}
                      </Typography>
                      <Typography variant="body2">
                        {t('key_creator_agreement2')}
                      </Typography>
                    </Stack>
                  </Stack>
                </AlertMain>
                <FormGroup>
                  <FormControlLabel
                    sx={{
                      '.Mui-disabled': {
                        fontWeight: 'bold',
                      },
                      '.MuiFormControlLabel-label': {
                        fontWeight: '700',
                      },
                    }}
                    control={
                      <Checkbox
                        id="i-users-bankaccountform-agree-terms-checkbox"
                        name=""
                        checked={agreeWithTerms}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          setAgreeWithTerms(event.target.checked)
                        }}
                      />
                    }
                    label={t('key_i_agree_to_the_terms')}
                  />
                </FormGroup>
              </Grid>
            </>
          )}
        </Grid>
      </StyledBaseContainer>
      <Stack
        sx={{ marginTop: '12px' }}
        justifyContent={'space-between'}
        alignItems={'center'}
        direction={'row'}
      >
        <Button
          id="b-user-back-to-dashboard"
          startIcon={<ChevronLeft />}
          sx={(theme) => ({
            color: theme.palette.grey[800],
            ':hover': {
              background: 'transparent',
              opacity: 0.7,
            },
          })}
          onClick={() => {
            unsubscribeToRouteChangeGuard()
            // In edit mode this form is step 0 of its own page, so "back" means
            // leaving it — pushed explicitly rather than via router.back(),
            // which depends on there being an in-app history entry and would
            // strand anyone who opened the edit URL directly.
            if (isEdit) push(PATH_DASHBOARD.general.verification.root)
            else setActiveStep(0)
          }}
        >
          {t('key_back')}
        </Button>
        <Button
          id="b-users-bankaccountform-submit"
          variant="contained"
          type="submit"
          sx={{ marginLeft: '8px' }}
          endIcon={<Iconify icon={'eva:arrow-ios-forward-fill'} />}
          size="large"
          disabled={
            !isValid ||
            isLoading ||
            !(isBankAccountNameDiff ? agreeWithTerms : true)
          }
        >
          {t('key_save_and_continue')}
        </Button>
      </Stack>
    </FormProvider>
  )
}
